import { createFileRoute, Link } from '@tanstack/react-router'
import { contentWrapper } from '../../../styles/contentWrapper';
import { pageTitle, sectionWrapper, titleWrapper } from '../../../styles/sectionStyles';
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import { ValidatableTextInputWithParser } from '../../../components/validatable-inputs/text-input';
import { ValidatableRangeInput } from '../../../components/validatable-inputs/range-input';
import { description, inputLabel, inputWrapper, nonRequiredFieldLabel, radio, requiredFieldLabel } from '../../../styles/inputStyles';
import { css } from '../../../../styled-system/css';
import { Checkbox } from '../../../components/inputs/checkbox';
import { button as buttonCva } from '../../../styles/buttonStyles';
import {
  type buttonVariants,
  createNotificationByPersonalInformationFormSchema,
  type createNotificationByPersonalInformationRequestSchema,
  defaultPushNotificationConfig
} from '../../../libs/admin/schemas/create-notification';
import type { z } from 'zod';
import { atom, Provider, useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import { BaseDialog } from '../../../components/dialog';
import { useCreateNotificationByPersonalInformation } from '../../../libs/admin/hooks';
import { transformNotificationByPersonalInformationRequest } from '../../../libs/utils/transform-notification-request';
import { NotificationPreview } from '../../../components/notification-preview';
import { NotificationContentForm } from '../../../components/notification-content-form';
import { ValidatableDayPicker } from '../../../components/validatable-inputs/day-picker';
import { LoadingOverlay } from '../../../components/loading-overlay';

export const Route = createFileRoute('/admin/notifications/new')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <Provider>
      <Layout />
    </Provider>
  );
}

const Layout = () => {
  const dirtyInfo = useAtomValue(dirtyInfoAtom);

  return (
    <div className={contentWrapper()}>
      <Title />
      <div className={css({
        display: 'flex',
        gap: '32px',
      })}>
        <Content />
        <NotificationPreview
          title={dirtyInfo.titleTemplate}
          message={dirtyInfo.messageTemplate}
          button={dirtyInfo.buttonTemplate.text}
          buttonVariant={dirtyInfo.buttonTemplate.variant}
          url={dirtyInfo.serviceUrlTemplate}
        />
      </div>
    </div>
  )
}

const Title = () => {
  return (
    <div className={titleWrapper()}>
      <h1 className={pageTitle()}>
        <Link to='/admin/notifications'>
          <ArrowBack />
        </Link>
        プッシュ通知作成
      </h1>
    </div>
  );
}

type DirtyPushNotificationInformation = z.infer<typeof createNotificationByPersonalInformationRequestSchema>
type ButtonVariant = z.infer<typeof buttonVariants>;

const dirtyInfoAtom = atom<DirtyPushNotificationInformation>({
  ...defaultPushNotificationConfig,
  personalInformation: {
    minAge: undefined,
    maxAge: undefined,
    addresses: [],
    genders: [],
  },
  scheduledAt: undefined,
});

const Content = () => {
  const [dirtyInfo, setDirtyInfo] = useAtom(dirtyInfoAtom);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [scheduleOption, setScheduleOption] = useState<'now' | 'scheduled'>('now');
  const createNotification = useCreateNotificationByPersonalInformation();
  const parsed = createNotificationByPersonalInformationFormSchema.safeParse({
    ...dirtyInfo,
    scheduleOption,
  });
  const errors = parsed.error?.format();
  const navigate = Route.useNavigate();
  const submitLabel = scheduleOption === 'scheduled' ? '送信予約する' : '送信する';
  const canSubmit = parsed.success;

  const handleConfirm = async () => {
    if (canSubmit) {
      try {
        const data = transformNotificationByPersonalInformationRequest(parsed.data);
        await createNotification.mutateAsync(data);
        setShowConfirmDialog(false);
        navigate({ to: '/admin/notifications' });
      } catch (error) {
        console.error('Failed to send notification:', error);
      }
    }
  };

  return (
    <>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
      })}>
        <div className={sectionWrapper()}>
          {/* 絞り込み対象 */}
          <ValidatableTextInputWithParser<string[]>
            title='対象者住所'
            description='住所をカンマ区切りで入力してください。指定しない場合、住所で絞り込まずに通知を送信します。'
            value={dirtyInfo.personalInformation.addresses}
            placeholder='青空県青空市, 青空県草原市'
            onChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                personalInformation: {
                  ...prev.personalInformation,
                  addresses: value || [],
                },
              }))
            }}
            parser={(value) => {
              return value.split(/[\s,，、]+/).filter((e) => e.length > 0)
            }}
            formatter={(value) => {
              return value.join(', ')
            }}
            type={'string'}
            validationMessage='OK'
          />
          <ValidatableRangeInput
            title="年齢範囲"
            description="指定しない場合、すべての年齢の利用者を対象に通知を送信します。"
            value={{
              min: dirtyInfo.personalInformation.minAge !== undefined ? Number(dirtyInfo.personalInformation.minAge) : undefined,
              max: dirtyInfo.personalInformation.maxAge !== undefined ? Number(dirtyInfo.personalInformation.maxAge) : undefined,
            }}
            onChange={(range) => {
              // update minAge, maxAge of personalInformation
              setDirtyInfo((prev) => ({
                ...prev,
                personalInformation: {
                  ...prev.personalInformation,
                  minAge: range.min !== undefined ? BigInt(range.min) : undefined,
                  maxAge: range.max !== undefined ? BigInt(range.max) : undefined,
                },
              }))
            }}
            unit="歳"
            isValid={errors?.personalInformation?.minAge === undefined && errors?.personalInformation?.maxAge === undefined}
            validationMessage="有効な範囲を入力してください"
          />
          <div className={inputWrapper()}>
            <div className={inputLabel()}>
              対象性別
              <span className={nonRequiredFieldLabel()}>任意</span>
            </div>
            <div className={description()}>
              指定しない場合、すべての性別の利用者を対象に通知を送信します。
            </div>
            <div className={css({
              display: "flex",
              alignItems: 'center',
              gap: '12px',
            })}>
              <Checkbox
                label='男性'
                checked={dirtyInfo.personalInformation.genders.includes('MALE')}
                onChange={(checked) => {
                  setDirtyInfo((prev) => ({
                    ...prev,
                    personalInformation: {
                      ...prev.personalInformation,
                      genders: checked
                        ? [...prev.personalInformation.genders, 'MALE']
                        : prev.personalInformation.genders.filter((g) => g !== 'MALE'),
                    },
                  }))
                }}
              />
              <Checkbox
                label='女性'
                checked={dirtyInfo.personalInformation.genders.includes('FEMALE')}
                onChange={(checked) => {
                  setDirtyInfo((prev) => ({
                    ...prev,
                    personalInformation: {
                      ...prev.personalInformation,
                      genders: checked
                        ? [...prev.personalInformation.genders, 'FEMALE']
                        : prev.personalInformation.genders.filter((g) => g !== 'FEMALE'),
                    },
                  }))
                }}
              />
              <Checkbox
                label='不明'
                checked={dirtyInfo.personalInformation.genders.includes('OTHER')}
                onChange={(checked) => {
                  setDirtyInfo((prev) => ({
                    ...prev,
                    personalInformation: {
                      ...prev.personalInformation,
                      genders: checked
                        ? [...prev.personalInformation.genders, 'OTHER']
                        : prev.personalInformation.genders.filter((g) => g !== 'OTHER'),
                    },
                  }))
                }}
              />
            </div>
          </div>
        </div>
        <div className={sectionWrapper()}>
          {/* 通知内容 */}
          <NotificationContentForm
            titleTemplateValue={dirtyInfo.titleTemplate}
            onTitleTemplateChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                titleTemplate: value,
              }))
            }}
            isTitleTemplateValid={errors?.titleTemplate === undefined}
            titleTemplateValidationMessage={errors?.titleTemplate ? errors.titleTemplate._errors[0] : undefined}

            messageTemplateValue={dirtyInfo.messageTemplate}
            onMessageTemplateChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                messageTemplate: value,
              }))
            }}
            isMessageTemplateValid={errors?.messageTemplate === undefined}
            messageTemplateValidationMessage={errors?.messageTemplate ? errors.messageTemplate._errors[0] : undefined}

            buttonVariantValue={dirtyInfo.buttonTemplate.variant}
            onButtonVariantChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                buttonTemplate: {
                  ...prev.buttonTemplate,
                  variant: value as ButtonVariant,
                }
              }))
            }}
            isButtonVariantValid={errors?.buttonTemplate?.variant === undefined}

            buttonTextValue={dirtyInfo.buttonTemplate.text}
            onButtonTextChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                buttonTemplate: {
                  ...prev.buttonTemplate,
                  text: value,
                }
              }))
            }}
            isButtonTextValid={errors?.buttonTemplate?.text === undefined}
            buttonTextValidationMessage={errors?.buttonTemplate?.text ? errors.buttonTemplate.text._errors[0] : undefined}

            buttonServiceIdValue={dirtyInfo.buttonTemplate.serviceId}
            onButtonServiceIdChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                buttonTemplate: {
                  ...prev.buttonTemplate,
                  serviceId: value,
                }
              }))
            }}

            buttonHrefValue={dirtyInfo.buttonTemplate.href}
            onButtonHrefChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                buttonTemplate: {
                  ...prev.buttonTemplate,
                  href: value,
                }
              }))
            }}
            isButtonHrefValid={errors?.buttonTemplate?.href === undefined}
            buttonHrefValidationMessage={errors?.buttonTemplate?.href ? errors.buttonTemplate.href._errors[0] : undefined}

            serviceUrlTemplateValue={dirtyInfo.serviceUrlTemplate}
            onServiceUrlTemplateChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                serviceUrlTemplate: value,
              }))
            }}
            isServiceUrlTemplateValid={errors?.serviceUrlTemplate === undefined}
            serviceUrlTemplateValidationMessage={errors?.serviceUrlTemplate ? errors.serviceUrlTemplate._errors[0] : undefined}
          />
          <div className={inputWrapper()}>
            <div className={inputLabel()}>
              送信予約
              <span className={requiredFieldLabel()}>必須</span>
            </div>
            <div className={css({
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
            })}>
              {[
                { value: 'now', label: '設定しない' },
                { value: 'scheduled', label: '設定する' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '4px',
                  })}
                >
                  <input
                    type="radio"
                    name="scheduleOption"
                    value={option.value}
                    checked={scheduleOption === option.value}
                    onChange={() => {
                      const nextValue = option.value as 'now' | 'scheduled';
                      setScheduleOption(nextValue);
                      if (nextValue === 'now') {
                        setDirtyInfo((prev) => ({
                          ...prev,
                          scheduledAt: undefined,
                        }));
                      }
                    }}
                    className={radio()}
                  />
                  <span className={css({ fontSize: '16px', color: 'text.primary' })}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            {scheduleOption === 'scheduled' && (
              <ValidatableDayPicker
                title="送信日時"
                value={dirtyInfo.scheduledAt}
                onChange={(value) => {
                  setDirtyInfo((prev) => ({
                    ...prev,
                    scheduledAt: value,
                  }));
                }}
                format='yyyy/MM/dd HH:mm'
                isValid={errors?.scheduledAt === undefined}
                isRequired
              />
            )}
          </div>
        </div>
        <button
          className={buttonCva({ color: 'primary' })}
          type='button'
          onClick={() => setShowConfirmDialog(true)}
          disabled={!canSubmit}
        >
          {submitLabel}
        </button>
      </div>

      <BaseDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="プッシュ通知を送信しますか？"
      >
        <div className={css({
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          marginTop: '24px',
        })}>
          <button
            className={buttonCva()}
            type='button'
            onClick={() => setShowConfirmDialog(false)}
          >
            キャンセル
          </button>
          <button
            className={buttonCva({ color: 'primary' })}
            type='button'
            onClick={handleConfirm}
            disabled={createNotification.isPending}
          >
            {submitLabel}
            {createNotification.isPending && <LoadingOverlay size='sm' />}
          </button>
        </div>
      </BaseDialog>
    </>
  )
}
